import { useQuery } from '@tanstack/react-query';
import { useTheme } from '@evi/skins';
import {
  DriverThemePicker,
  buildDriverPickerEntries,
  type DriverPickerEntry,
} from '@evi/ui';
import { fetchProfile, fetchUnlocks } from '@/lib/api';
import { useApplyTheme } from '@/hooks/useApplyTheme';

/**
 * Self-contained DriverThemePicker wrapper. Owns the data
 * wiring (profile recent-themes, unlocks catalogue, active
 * theme family) so the picker can be mounted anywhere — not
 * just inside ProfilePage's local state — without forcing
 * each call site to repeat the same fetch + handler scaffold.
 *
 * Lifted out of ProfilePage when the AppShell global top bar
 * shipped (commit splits the picker mount up to chrome level
 * so it's reachable on every menu page, not just Profile).
 *
 * onNavigate is the only prop because the locked-theme +
 * view-all flows route into the dedicated /themes page —
 * routing is the only AppShell-level concern the container
 * can't resolve internally.
 */
interface DriverThemePickerContainerProps {
  onNavigate: (path: string) => void;
}

export function DriverThemePickerContainer({ onNavigate }: DriverThemePickerContainerProps) {
  const { theme: activeFamily } = useTheme();
  const { applyTheme: runApply } = useApplyTheme();

  const profileQ = useQuery({
    queryKey: ['profile'],
    queryFn: fetchProfile,
    // Shared cache key with ProfilePage's own profile query —
    // both surfaces read the same source-of-truth snapshot,
    // and `recent_themes` updates from one will be reflected
    // on the other without a duplicate round-trip.
  });

  const unlocksQ = useQuery({
    queryKey: ['unlocks'],
    queryFn: fetchUnlocks,
    staleTime: 60_000,
  });

  const pickerEntries: DriverPickerEntry[] = buildDriverPickerEntries({
    activeFamilyId: activeFamily.id,
    activeSkinCode: unlocksQ.data?.activeSkinCode ?? null,
    unlocksCatalogue: unlocksQ.data?.unlocks ?? [],
  });

  const applyTheme = (entry: DriverPickerEntry): Promise<void> => runApply(entry);
  const openLockedTheme = (entry: DriverPickerEntry): void => {
    onNavigate(`/themes?theme=${encodeURIComponent(entry.code)}`);
  };
  const openAllThemes = (): void => {
    onNavigate('/themes');
  };

  return (
    <DriverThemePicker
      entries={pickerEntries}
      recentCodes={profileQ.data?.recent_themes ?? []}
      onApply={applyTheme}
      onOpenLocked={openLockedTheme}
      onOpenAll={openAllThemes}
    />
  );
}
